import React from 'react';
import DateOfBirthSelector from './DateOfBirthSelector';
import AddressAutofill from './AddressAutofill';

const BasicInfoTab = ({ formData, handleInputChange, handleAddressChange, FormField }) => {
  return (
    <div className="bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl shadow-slate-200/50 rounded-3xl p-8 overflow-hidden relative">
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/40 via-transparent to-indigo-50/40 pointer-events-none" />

      {/* Header */}
      <div className="relative flex items-center gap-4 mb-8">
        <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30">
          <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Personal Information</h2>
          <p className="text-slate-500 mt-1">Basic details and contact information</p>
        </div>
      </div>

      <div className="relative space-y-8">
        {/* Basic Fields */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* First Name with Honorific */}
          <div className="space-y-2">
            <label htmlFor="first_name" className="text-sm font-semibold text-slate-700 after:content-['*'] after:text-red-500 after:ml-1">
              First Name
            </label>
            <div className="flex gap-2">
              <select
                id="honorific"
                value={formData.honorific || formData.title || ''}
                onChange={(e) => handleInputChange('honorific', e.target.value)}
                className="w-24 h-12 px-3 bg-white border border-slate-200 rounded-xl
                  focus:border-violet-400 focus:ring-4 focus:ring-violet-100 focus:shadow-lg focus:shadow-violet-100/50
                  hover:border-slate-300 hover:shadow-sm
                  transition-all duration-200 ease-out text-sm cursor-pointer"
                title="Honorific"
              >
                <option value="">--</option>
                <option value="Mr.">Mr.</option>
                <option value="Mrs.">Mrs.</option>
                <option value="Ms.">Ms.</option>
                <option value="Miss.">Miss.</option>
                <option value="Dr.">Dr.</option>
                <option value="Prof.">Prof.</option>
                <option value="Sir">Sir</option>
              </select>
              <input
                id="first_name"
                type="text"
                value={formData.first_name || ''}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                placeholder="First name"
                required
                className="flex-1 h-12 px-4 bg-white border border-slate-200 rounded-xl
                  focus:border-violet-400 focus:ring-4 focus:ring-violet-100 focus:shadow-lg focus:shadow-violet-100/50
                  hover:border-slate-300 hover:shadow-sm
                  transition-all duration-200 ease-out placeholder:text-slate-400"
              />
            </div>
          </div>

          <FormField
            id="last_name"
            label="Last Name"
            value={formData.last_name || ''}
            onChange={(e) => handleInputChange('last_name', e.target.value)}
            required
            placeholder="Last name"
          />

          <FormField
            id="company_title"
            label="Company Title"
            value={formData.company_title || ''}
            onChange={(e) => handleInputChange('company_title', e.target.value)}
            placeholder="e.g., CEO, VP of Sales, Director"
          />

          <FormField
            id="email"
            label="Primary Work Email"
            type="email"
            value={formData.email || ''}
            onChange={(e) => handleInputChange('email', e.target.value)}
            required
            placeholder="executive@company.com"
          />

          <FormField
            id="phone"
            label="Office Phone"
            value={formData.phone || ''}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            placeholder="+1 (555) 123-4567"
          />

          <FormField
            id="mobile"
            label="Mobile Number"
            value={formData.mobile || ''}
            onChange={(e) => handleInputChange('mobile', e.target.value)}
            placeholder="+1 (555) 987-6543"
          />

          <FormField
            id="company"
            label="Company"
            value={formData.company || ''}
            onChange={(e) => handleInputChange('company', e.target.value)}
            placeholder="Company name"
          />

          <FormField
            id="department"
            label="Department"
            value={formData.department || ''}
            onChange={(e) => handleInputChange('department', e.target.value)}
            placeholder="Department"
          />
        </div>

        {/* Other Email Addresses Section */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <label className="text-sm font-bold text-slate-700">Other Email Addresses</label>
            <span className="text-xs text-slate-500">(personal, alternate work, etc.)</span>
          </div>

          <div className="space-y-3">
            {(formData.other_emails || []).map((email, index) => (
              <div key={index} className="flex gap-2 group">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    const newEmails = [...(formData.other_emails || [])];
                    newEmails[index] = e.target.value;
                    handleInputChange('other_emails', newEmails);
                  }}
                  placeholder="email@example.com"
                  className="flex-1 h-12 px-4 bg-white border border-slate-200 rounded-xl
                    focus:border-violet-400 focus:ring-4 focus:ring-violet-100 focus:shadow-lg focus:shadow-violet-100/50
                    hover:border-slate-300 hover:shadow-sm
                    transition-all duration-200 ease-out placeholder:text-slate-400"
                />
                <button
                  type="button"
                  onClick={() => {
                    const newEmails = (formData.other_emails || []).filter((_, i) => i !== index);
                    handleInputChange('other_emails', newEmails);
                  }}
                  className="px-3 h-12 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200 opacity-50 group-hover:opacity-100"
                  title="Remove email"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={() => {
                const newEmails = [...(formData.other_emails || []), ''];
                handleInputChange('other_emails', newEmails);
              }}
              className="flex items-center gap-2 text-sm text-violet-600 hover:text-violet-700 font-semibold px-4 py-2.5 hover:bg-violet-50 rounded-xl transition-all duration-200 border-2 border-dashed border-violet-200 hover:border-violet-300 w-full justify-center"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add another email address
            </button>
          </div>

          <p className="text-xs text-slate-500 mt-2">
            Emails from any of these addresses will be automatically associated with this executive.
          </p>
        </div>

        {/* Contact Address Section */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <label className="text-sm font-bold text-slate-700">Contact Address</label>
          </div>
          <AddressAutofill
            address={formData.address}
            onChange={(newAddress) => handleAddressChange(newAddress)}
          />
        </div>

        {/* Personal Details */}
        <div className="grid md:grid-cols-2 gap-6">
          <DateOfBirthSelector
            id="date_of_birth"
            value={formData.date_of_birth || ''}
            onChange={(value) => handleInputChange('date_of_birth', value)}
          />

          <div className="space-y-2">
            <label htmlFor="gender" className="text-sm font-semibold text-slate-700">Gender</label>
            <select
              value={formData.gender || ""}
              onChange={(e) => handleInputChange('gender', e.target.value)}
              className="w-full h-12 px-4 bg-white border border-slate-200 rounded-xl cursor-pointer
                focus:border-violet-400 focus:ring-4 focus:ring-violet-100 focus:shadow-lg focus:shadow-violet-100/50
                hover:border-slate-300 hover:shadow-sm
                transition-all duration-200 ease-out"
            >
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
              <option value="prefer_not_to_say">Prefer not to say</option>
            </select>
          </div>

          <div className="space-y-2">
            <label htmlFor="is_active" className="text-sm font-semibold text-slate-700">Status</label>
            <select
              value={formData.is_active ? "active" : "inactive"}
              onChange={(e) => handleInputChange('is_active', e.target.value === "active")}
              className="w-full h-12 px-4 border border-slate-200 rounded-lg focus:border-blue-400 focus:ring-blue-400"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Assistant Notes */}
        <div className="space-y-2">
          <label htmlFor="assistant_notes" className="text-sm font-semibold text-slate-700">Assistant Notes</label>
          <textarea
            id="assistant_notes"
            value={formData.assistant_notes || ''}
            onChange={(e) => handleInputChange('assistant_notes', e.target.value)}
            className="w-full h-32 px-4 py-3 border border-slate-200 rounded-lg focus:border-blue-400 focus:ring-blue-400 resize-none"
            placeholder="Private notes about preferences, special requirements, etc."
          />
        </div>
      </div>
    </div>
  );
};

export default BasicInfoTab;
